import {
  isRefreshUnavailableError,
  isSessionChangedError,
  isSessionEndedError,
} from './refreshFailure';

/**
 * `retryIf` del `RetryLink`, extraído para poder probarlo (`client.tsx` crea el
 * cliente al importarse).
 *
 * Un aborto es el plazo de una fase que venció (`phase2Transport`): no es un
 * endpoint caído. Sin esta salida cada plazo vencido contaría como fallo del
 * endpoint y el failover multi-endpoint podría conmutar por una red lenta; y el
 * `RetryLink` reintentaría cinco veces un `fetch` cuyo `signal` ya está
 * abortado, sumando retardos al encolado (spec 2026-09-08-fase2-encolada § 2 D7).
 */
export function shouldRetryOperation(
  error: unknown,
  deps: {
    isOnlineNow: () => boolean;
    isServerDown: () => boolean;
    /** Failover multi-endpoint (`apiEndpointManager`). */
    reportFailure: (error: unknown) => void;
    /** Cortacircuitos «no contesta ningún servidor». */
    reportServerFailure: (error: unknown) => void;
    /** ¿La petición ya se abortó (plazo vencido con motivo propio)? */
    wasAborted?: () => boolean;
  },
): boolean {
  const e = error as { offlineQueued?: boolean; name?: string } | null;
  if (e?.offlineQueued) return false;
  if (e?.name === 'AbortError') return false;
  // Un plazo vencido aborta con SU error como motivo (`MutationDeadlineError`),
  // no con un `AbortError`: sin esto se reintentaba 4 veces sobre un `signal`
  // ya abortado y cada rechazo le decía al cortacircuitos «el servidor
  // contestó» (revisión del 9-oct, H8).
  if (deps.wasAborted?.()) return false;
  // Spec 2026-10-01 § 5.2: reintentar sobre la sesión solo multiplica los
  // refrescos (y los eventos de Sentry: 77 en 30 s en el incidente del 1-oct).
  // La sesión muerta no va a revivir, y el refresco no disponible ya se intentó.
  if (
    isSessionEndedError(error) ||
    isRefreshUnavailableError(error) ||
    isSessionChangedError(error)
  ) {
    return false;
  }
  if (!deps.isOnlineNow()) return false;
  // El failover oye los fallos también con el corte abierto: en la web hay
  // respaldo, y si el corte le tapara los fallos no volvería a sondearlo hasta
  // el latido (revisión del 9-oct, H4). Solo cuentan red y 502-504 (filtra él).
  deps.reportFailure(error);
  // Cortacircuitos abierto (spec 2026-10-09 D4): lo confirmó él —dos fallos y
  // dos sondeos sin respuesta—. Reintentar solo retrasa lo guardado (≈ 4,5 s
  // por consulta) y la cola; la salida la da el latido o una respuesta.
  if (deps.isServerDown()) return false;
  deps.reportServerFailure(error);
  return !!error;
}
